import type {
  ChatAttachment,
  ChatAttachmentUrl,
  ChatConversation,
  ChatConversationSession,
  ChatCarryOverOffer,
  ChatMessage,
  ChatReaction,
  CustomerProfile,
  SendMessageRequest,
} from './entity';
import { ChatHttpError } from './errors';

// Supplies the customer session for customer-scoped requests when the
// customer is known through the Flix Identity bridge rather than a Shopify
// customer session (see widget/flixIdentityAuth.ts). Absent for a Shopify
// session, where the App Proxy's own signature carries the identity and no
// header is needed at all.
export interface CustomerAuth {
  // The current session token, or null when there is none.
  getToken(): string | null;
  // Establishes a fresh session after the current one was refused (they
  // live ten minutes). Resolves to the new token, or null when the customer
  // is no longer signed in.
  refresh(): Promise<string | null>;
}

export interface HttpClientConfig {
  baseUrl: string;
  customerAuth?: CustomerAuth;
}

export interface HttpClient {
  createConversation(customerId: string): Promise<ChatConversationSession>;
  getConversation(conversationId: string, token: string): Promise<ChatConversation>;
  // No token — identity comes from the Shopify App Proxy signature attached
  // automatically to every request once baseUrl is the proxy path, the same
  // way createConversation's own identity does. There's no per-conversation
  // session token that could authorize a customer-scoped (not
  // conversation-scoped) list.
  listConversations(): Promise<ChatConversation[]>;
  listMessages(conversationId: string, token: string): Promise<ChatMessage[]>;
  // Same no-token reasoning as listConversations — for browsing an old,
  // already-closed conversation with no live session token.
  getConversationHistory(conversationId: string): Promise<ChatMessage[]>;
  sendMessage(request: SendMessageRequest, token: string): Promise<ChatMessage>;
  uploadAttachment(
    conversationId: string,
    file: Blob,
    filename: string,
    token: string,
    // Reports upload progress as a 0–1 fraction of bytes sent. Optional so
    // existing callers keep working; when supplied, the request uses
    // XMLHttpRequest (the only browser API that exposes upload progress —
    // fetch does not).
    onProgress?: (fraction: number) => void,
  ): Promise<ChatAttachment>;
  downloadAttachment(fileId: string, token: string): Promise<Blob>;
  // For media elements that stream directly from object storage — they
  // can't send the Authorization header downloadAttachment relies on.
  getAttachmentUrl(fileId: string, token: string): Promise<ChatAttachmentUrl>;
  addReaction(messageId: string, emoji: string, token: string): Promise<ChatReaction[]>;
  removeReaction(messageId: string, emoji: string, token: string): Promise<ChatReaction[]>;
  reportMessage(messageId: string, token: string): Promise<void>;
  resendMessage(messageId: string, token: string): Promise<ChatMessage>;
  listCarryOverOffers(token: string): Promise<ChatCarryOverOffer[]>;
  carryOverMessage(messageId: string, token: string): Promise<ChatMessage | null>;
  reopenConversation(conversationId: string, token: string): Promise<ChatConversationSession>;
  closeConversation(conversationId: string, token: string): Promise<ChatConversation>;
  // Same no-token reasoning as listConversations/getConversationHistory —
  // customer-scoped, identity comes from the App Proxy signature.
  getCustomerProfile(): Promise<CustomerProfile>;
  setCustomerName(name: string): Promise<CustomerProfile>;
}

interface ErrorResponseBody {
  error?: { message?: string; code?: string };
}

// The one refusal that renewing can fix: the server saying, in so many
// words, that the customer session it was shown is expired or unknown.
const CUSTOMER_SESSION_EXPIRED_CODE = 'customer_session_invalid';

function isErrorResponseBody(value: unknown): value is ErrorResponseBody {
  return typeof value === 'object' && value !== null;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const body: unknown = text.length > 0 ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const message =
      isErrorResponseBody(body) && typeof body.error?.message === 'string'
        ? body.error.message
        : `Request failed with status ${response.status}.`;
    const code =
      isErrorResponseBody(body) && typeof body.error?.code === 'string' ? body.error.code : null;

    throw new ChatHttpError(response.status, message, code);
  }

  return body as T;
}

function authHeader(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export function createHttpClient(config: HttpClientConfig): HttpClient {
  const baseUrl = config.baseUrl.replace(/\/+$/, '');

  async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });

    return parseJsonResponse<T>(response);
  }

  // For the customer-scoped routes: the ones whose caller is "this
  // customer", not "this conversation". With a Shopify session nothing is
  // added. With a bridge session the token rides along, and a 401 is given
  // exactly one chance to recover by renewing it — a session that lapsed
  // mid-page is routine, and the customer should not see it happen.
  async function requestCustomerJson<T>(path: string, init?: RequestInit): Promise<T> {
    const customerAuth = config.customerAuth;
    const token = customerAuth?.getToken() ?? null;

    if (!customerAuth || token === null) {
      return requestJson<T>(path, init);
    }

    try {
      return await requestJson<T>(path, {
        ...init,
        headers: { ...init?.headers, ...authHeader(token) },
      });
    } catch (error) {
      // Renewed only for an expired session, and only once. Any other
      // refusal — a missing App Proxy signature, a forbidden conversation,
      // a rate limit, a 401 with some other cause — is an answer, not a
      // lapse, and is passed on untouched: retrying it with a fresh session
      // could only turn "no" into a second "no", or worse.
      if (
        !(error instanceof ChatHttpError) ||
        error.status !== 401 ||
        error.code !== CUSTOMER_SESSION_EXPIRED_CODE
      ) {
        throw error;
      }

      const renewed = await customerAuth.refresh();
      if (renewed === null) {
        throw error;
      }

      return requestJson<T>(path, {
        ...init,
        headers: { ...init?.headers, ...authHeader(renewed) },
      });
    }
  }

  return {
    createConversation(customerId: string): Promise<ChatConversationSession> {
      return requestCustomerJson<ChatConversationSession>('/chat/conversations', {
        method: 'POST',
        body: JSON.stringify({ customerId }),
      });
    },

    getConversation(conversationId: string, token: string): Promise<ChatConversation> {
      return requestJson<ChatConversation>(
        `/chat/conversations/${encodeURIComponent(conversationId)}`,
        { headers: authHeader(token) },
      );
    },

    listConversations(): Promise<ChatConversation[]> {
      return requestCustomerJson<ChatConversation[]>('/chat/conversations');
    },

    listMessages(conversationId: string, token: string): Promise<ChatMessage[]> {
      return requestJson<ChatMessage[]>(
        `/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
        { headers: authHeader(token) },
      );
    },

    getConversationHistory(conversationId: string): Promise<ChatMessage[]> {
      return requestCustomerJson<ChatMessage[]>(
        `/chat/conversations/${encodeURIComponent(conversationId)}/history`,
      );
    },

    sendMessage(request: SendMessageRequest, token: string): Promise<ChatMessage> {
      return requestJson<ChatMessage>('/chat/messages', {
        method: 'POST',
        headers: authHeader(token),
        body: JSON.stringify(request),
      });
    },

    uploadAttachment(
      conversationId: string,
      file: Blob,
      filename: string,
      token: string,
      onProgress?: (fraction: number) => void,
    ): Promise<ChatAttachment> {
      const formData = new FormData();
      formData.append('conversationId', conversationId);
      formData.append('file', file, filename);

      // XMLHttpRequest rather than fetch: only XHR exposes upload progress
      // (xhr.upload 'progress' events). Error/status handling is kept
      // byte-for-byte equivalent to parseJsonResponse so the widget's
      // existing 400-vs-generic branch (see uploadAttachment in widget.ts)
      // behaves identically.
      return new Promise<ChatAttachment>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${baseUrl}/chat/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        // Content-Type is intentionally left unset — the browser fills in
        // the multipart/form-data boundary itself.

        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              onProgress(event.loaded / event.total);
            }
          });
        }

        xhr.addEventListener('load', () => {
          const text = xhr.responseText;
          let body: unknown;
          try {
            body = text.length > 0 ? JSON.parse(text) : undefined;
          } catch {
            body = undefined;
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(body as ChatAttachment);
            return;
          }

          const message =
            isErrorResponseBody(body) && typeof body.error?.message === 'string'
              ? body.error.message
              : `Request failed with status ${xhr.status}.`;
          reject(new ChatHttpError(xhr.status, message));
        });

        xhr.addEventListener('error', () =>
          reject(new ChatHttpError(0, 'Network error during upload.')),
        );
        xhr.addEventListener('abort', () => reject(new ChatHttpError(0, 'Upload was cancelled.')));

        xhr.send(formData);
      });
    },

    async downloadAttachment(fileId: string, token: string): Promise<Blob> {
      const response = await fetch(`${baseUrl}/chat/files/${encodeURIComponent(fileId)}`, {
        headers: authHeader(token),
      });

      if (!response.ok) {
        // Error responses are still JSON (see mapErrorToHttpResponse) even
        // though a successful response here is raw binary.
        const text = await response.text();
        const body: unknown = text.length > 0 ? JSON.parse(text) : undefined;
        const message =
          isErrorResponseBody(body) && typeof body.error?.message === 'string'
            ? body.error.message
            : `Request failed with status ${response.status}.`;

        throw new ChatHttpError(response.status, message);
      }

      return response.blob();
    },

    getAttachmentUrl(fileId: string, token: string): Promise<ChatAttachmentUrl> {
      return requestJson<ChatAttachmentUrl>(`/chat/files/${encodeURIComponent(fileId)}/url`, {
        headers: authHeader(token),
      });
    },

    async addReaction(messageId: string, emoji: string, token: string): Promise<ChatReaction[]> {
      const body = await requestJson<{ reactions: ChatReaction[] }>(
        `/chat/messages/${encodeURIComponent(messageId)}/reactions`,
        { method: 'POST', headers: authHeader(token), body: JSON.stringify({ emoji }) },
      );
      return body.reactions;
    },

    async removeReaction(messageId: string, emoji: string, token: string): Promise<ChatReaction[]> {
      const body = await requestJson<{ reactions: ChatReaction[] }>(
        `/chat/messages/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(emoji)}`,
        { method: 'DELETE', headers: authHeader(token) },
      );
      return body.reactions;
    },

    async reportMessage(messageId: string, token: string): Promise<void> {
      // 204 No Content — requestJson tolerates an empty body.
      await requestJson<undefined>(`/chat/messages/${encodeURIComponent(messageId)}/report`, {
        method: 'POST',
        headers: authHeader(token),
      });
    },

    resendMessage(messageId: string, token: string): Promise<ChatMessage> {
      return requestJson<ChatMessage>(`/chat/messages/${encodeURIComponent(messageId)}/resend`, {
        method: 'POST',
        headers: authHeader(token),
      });
    },

    async listCarryOverOffers(token: string): Promise<ChatCarryOverOffer[]> {
      const body = await requestJson<{ offers: ChatCarryOverOffer[] }>('/chat/carryover', {
        method: 'GET',
        headers: authHeader(token),
      });
      return body.offers;
    },

    async carryOverMessage(messageId: string, token: string): Promise<ChatMessage | null> {
      const body = await requestJson<{ message: ChatMessage | null }>(
        `/chat/carryover/${encodeURIComponent(messageId)}`,
        { method: 'POST', headers: authHeader(token) },
      );
      return body.message;
    },

    reopenConversation(conversationId: string, token: string): Promise<ChatConversationSession> {
      return requestJson<ChatConversationSession>(
        `/chat/conversations/${encodeURIComponent(conversationId)}/reopen`,
        { method: 'POST', headers: authHeader(token) },
      );
    },

    closeConversation(conversationId: string, token: string): Promise<ChatConversation> {
      return requestJson<ChatConversation>(
        `/chat/conversations/${encodeURIComponent(conversationId)}/close`,
        { method: 'POST', headers: authHeader(token) },
      );
    },

    getCustomerProfile(): Promise<CustomerProfile> {
      return requestCustomerJson<CustomerProfile>('/chat/customer');
    },

    setCustomerName(name: string): Promise<CustomerProfile> {
      return requestCustomerJson<CustomerProfile>('/chat/customer/name', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
    },
  };
}
